/**
 * helper function that creates formDefinition object from JSON schema and
 * additional user settings (formRules)
 */
import {
  each,
  filter,
  find,
  flatten,
  get,
  has,
  includes,
  isBoolean,
  isEmpty,
  map,
  merge,
  omit,
  set,
  startCase,
} from 'lodash';
import { FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  observableCombineLatest,
  observableEMPTY,
  observableOf,
  rx,
  Subject,
} from '@shared/rx';
import {
  FieldList,
  IFieldPreferences,
  IFormGroupPreferences,
  IFormGroupRules,
  IFormRules,
  IWidgetAction,
  PredicateFn,
  SchemaNode,
  WIDGET_COMPONENT,
  IWidgetEvent,
} from '@shared/types';
import {
  addWidgetSpecificData,
  composeSelectOptions,
  createDeferedValueObservable,
  dynamicPropertyToObservable,
  extractSchemaFields,
  getFormControl,
  getLocalSchemaProperties,
  isRequired,
  normalizeDynamicProperty,
  preferredFieldWidgetType,
  removeRedundantDescriptions,
  resolveGroupLinks,
} from './schema-form-functions';
import { widgetOption } from './widget-options';
import { asObservable, isObservable } from '@shared/utils';
import { createWidgetControlProxy } from './widget-control-proxy';
import { createFormActionObs } from './form-action-emitter';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

/**
 * Create form definition from scheme and form rules.
 *
 * @returns
 */
export const createFormDefinitionsFromSchema = (
  params: {
    schema?: SchemaNode;
    rules?: IFormRules;
    form?: FormGroup;
    eventBus$?: Observable<IWidgetEvent>;
    fieldsToSetDisable?: string[];
    i18nService: XVII18nService;
    /**
     * Whether to keep the same reference for groups and fields objects if they are not changed after translation. It can be useful for performance optimization in case of big forms with many fields, but should be used with caution as it can lead to unexpected behavior in some cases.
     */
    keepReference?: boolean;
  },
  // eslint-disable-next-line max-params
): IFormGroupPreferences[] => {
  const {
    schema = {},
    rules = {},
    form = null,
    eventBus$ = observableEMPTY,
    fieldsToSetDisable = [],
    i18nService,
    keepReference = false,
  } = params;

  const action = new Subject<IWidgetAction>();

  // if form is provided - create a subscription for value\status changed to pass these action to action
  if (form) {
    const formChangesObs = createFormActionObs(form);
    formChangesObs.subscribe(action);
  }
  const translatedRules: IFormRules =
    i18nService.translateTextInFormRules(rules);

  const {
    groups: originalGroups = [],
    fields = {},
    defaultGroup = i18nService.instant(I18n.Common.General),
  } = translatedRules || {};

  // adds default empty fields array into every group
  const groups: any = originalGroups.map((group) =>
    Object.assign(
      {
        fields: [],
        hiddenObs: null,
        disabledObs: null,
      },
      group,
    ),
  );

  const localSchemaProperties = getLocalSchemaProperties(translatedRules);

  const schemaWithLocalProps = merge({}, schema, {
    properties: localSchemaProperties,
  });

  const allFields: FieldList = extractSchemaFields(schemaWithLocalProps);
  const allFieldPaths: string[] = Object.keys(allFields);

  // Observable used as the source of formValues for dynamic properties mappers
  const formValueObs: Observable<any> = createDeferedValueObservable(form);

  /**
   * fillFieldDefaults
   *
   * combines following data into a single object:
   *   default field preferences,
   *   schema node data,
   *   user defined data from rules
   *
   * @returns {IFieldPreferences}
   */

  const composeFieldProperties: any = (path: string): IFieldPreferences => {
    const name: string = path.split('.').pop();
    const fieldRules: any = get(fields, path, {});
    const fieldPath = get(fieldRules, 'path', path);
    const schemaNode: SchemaNode = allFields[fieldPath] || {};
    const schemaPrefs: any = merge(
      { path, name },
      omit(schemaNode, 'properties', 'required'),
    );
    const widget: WIDGET_COMPONENT = get(
      fieldRules,
      'widget',
      get(schemaNode, 'widget', preferredFieldWidgetType(schemaPrefs)),
    );

    const control = getFormControl(form, fieldPath);
    const widgetMappers = get(fieldRules, 'widgetData.widgetMappers');
    const preferences: any = Object.assign(
      {
        widget,
        action,
        layout: get(fieldRules, 'layout'),
        control: createWidgetControlProxy(control, widgetMappers),
        title: widgetOption(widget).autoTitle ? startCase(name) : '',
        schema: schemaNode,
        required: isRequired(schema, fieldPath),
        tooltipContainer: 'body',
      },
      schemaPrefs,
      fieldRules,
      {
        titleObs: get(
          fieldRules,
          'titleObs',
          new BehaviorSubject(
            get(
              fieldRules,
              'title',
              get(
                schemaPrefs,
                'title',
                widgetOption(widget).autoTitle ? startCase(name) : '',
              ),
            ),
          ),
        ),
        eventBus$: eventBus$.pipe(
          rx.filter((event) => event.widgetFieldPath === path),
        ),
      },
    );

    return preferences;
  };

  const hideDeprecatedFields: any = (
    field: IFieldPreferences,
  ): IFieldPreferences => {
    if (has(field, 'deprecationInfo')) {
      return Object.assign({}, field, {
        show: get(field, 'show', false),
        widget: get(field, 'widget', WIDGET_COMPONENT.input),
      });
    }

    return field;
  };

  const addVisibilityHandler: any = (
    fieldProps: IFieldPreferences,
  ): Observable<any> | IFieldPreferences => {
    // The default show value is a function that returns true.
    // In opposite to a plain value, function predicate will
    // pay attention to control presence and status.
    const {
      control,
      show = (): boolean => true,
      widget,
      hiddenObs,
    } = fieldProps;
    // Use hiddenObs observable if provided. Ignore show parameter.
    if (isObservable(hiddenObs)) {
      return fieldProps;
    }
    const needsControl: boolean = widgetOption(widget).hasControl;
    /* eslint-disable-next-line */
    const showPredicate: boolean | PredicateFn = normalizeDynamicProperty(
      show as PredicateFn,
    );

    const getHiddenObs: Function = (): Observable<boolean> => {
      // Always use static 'show' value if provided.
      // The control presence and state will be ignored.
      // WARNING. It is possible to display widgets, that are not intended
      // to be used without a control.
      if (isBoolean(showPredicate)) {
        return observableOf(!showPredicate);
      }

      // Always hide a field if it needs a control but does not have one.
      // Not necessary but saves some resources by preventing excessive emits
      if (needsControl && !control) {
        return observableOf(true);
      }

      // Common case.
      // Sometimes it looks like (needsControl && control.disabled) does not
      // need to be update on each formValue change, but it DOES.
      // Disabled controls values are excluded from the combined form value, so watching
      // form.valueChanges is an easy way to also react to control.disabled state changes.
      return formValueObs.pipe(
        rx.map(
          (formValue) =>
            (needsControl && control.disabled) || !showPredicate(formValue),
        ),
        rx.distinctUntilChanged(),
        rx.share(),
      );
    };

    return Object.assign(fieldProps, { hiddenObs: getHiddenObs() });
  };

  const addDisabledHandler: any = (
    fieldProps: IFieldPreferences,
    fieldsToDisable: string[] = [],
  ): IFieldPreferences => {
    if (includes(fieldsToDisable, fieldProps.path)) {
      return Object.assign(fieldProps, { disabledObs: observableOf(true) });
    }
    if (isObservable(get(fieldProps, 'disabledObs'))) {
      return fieldProps;
    }
    const disabledObs: Observable<any> = dynamicPropertyToObservable(
      formValueObs,
      get(fieldProps, 'disabled'),
    );

    return Object.assign(fieldProps, { disabledObs });
  };

  /**
   *  Appends action emitter to every group
   */
  const addActionEmitter = (
    group: IFormGroupPreferences,
  ): IFormGroupPreferences => {
    return {
      ...group,
      action,
    };
  };

  /**
   *  Casts group name to Observable if it is not already one
   */
  const addTitleObservable = (group: IFormGroupRules): IFormGroupRules => {
    let titleObs;

    if (group.name || group.titleObs) {
      titleObs = group.titleObs ? group.titleObs : asObservable(group.name);
    }

    return titleObs ? { ...group, titleObs } : group;
  };
  /**
   * fills each field in fields property with default data
   */
  const fillGroupFields = (
    group: IFormGroupPreferences,
    fieldsToDisable: string[] = [],
  ): IFormGroupPreferences =>
    Object.assign({}, group, {
      fields: group.fields
        .map(composeFieldProperties)
        .map(hideDeprecatedFields)
        .map(addWidgetSpecificData)
        .map(composeSelectOptions)
        .map((field) => addDisabledHandler(field, fieldsToDisable))
        .map(addVisibilityHandler)
        .map(removeRedundantDescriptions),
    });

  const userDefinedFieldPaths: any[] = [].concat(...map(groups, 'fields'));

  const defaultFieldPaths: string[] = allFieldPaths.filter(
    (fieldPath) => !userDefinedFieldPaths.includes(fieldPath),
  );

  // TODO refactor the defaultGroup options to point on group ID instead of name
  if (defaultGroup !== false) {
    const defaultGroupObject: any = groups.find(
      ({ name, id }) => name === defaultGroup || id === 'default',
    );

    if (defaultGroupObject) {
      defaultGroupObject.fields = (defaultGroupObject.fields || []).concat(
        defaultFieldPaths,
      );
    } else {
      groups.push({
        id: 'default',
        name: defaultGroup.toString(),
        fields: defaultFieldPaths,
        hiddenObs: null,
        disabledObs: null,
        titleObs: observableOf(defaultGroup.toString()),
      });
    }
  }

  const mappedGroups: any = groups
    // NOTE: action emitter in groups is kinda useless, however its easisest
    // way to pass it to the base container.
    // Can be removed once schemForm is converted into a service
    .map(addActionEmitter)
    .map((group) => fillGroupFields(group, fieldsToSetDisable))
    .map(addTitleObservable);
  mappedGroups.forEach(resolveGroupLinks);

  const addChangesToGroup = (
    group: IFormGroupPreferences,
  ): IFormGroupPreferences => {
    if (has(group, 'id')) return group;

    const relatedChanges$: Observable<any>[] = [];
    const relatedChanges: any[] = [];

    const editSectionNames: string[] = map(group.fields, 'name');

    each(editSectionNames, (fieldName: string) => {
      const item = find(mappedGroups, { id: fieldName });
      if (!item) return;

      const fieldsWithChanges = filter(get(item, 'fields'), (field) =>
        has(field, 'widgetData.changes'),
      );
      if (isEmpty(fieldsWithChanges)) return { ...group };

      each(fieldsWithChanges, (field) => {
        const changes = field?.widgetData?.changes;

        if (!changes) return;

        relatedChanges$.push(changes);
      });

      observableCombineLatest([...flatten(relatedChanges$)]).subscribe(
        (data: any[][]) => {
          relatedChanges.push(...flatten(data));
        },
      );
    });

    return { ...group, relatedChanges };
  };

  const groupsWithChanges: IFormGroupPreferences[] =
    mappedGroups.map(addChangesToGroup);

  groupsWithChanges.forEach((group) => {
    const { hidden = false, disabled = false } = group;
    set(group, 'hiddenObs', dynamicPropertyToObservable(formValueObs, hidden));
    set(
      group,
      'disabledObs',
      dynamicPropertyToObservable(formValueObs, disabled),
    );
  });

  return i18nService.translateTextInFormGroups(groupsWithChanges, {
    keepReference,
  });
};
